import { Modal, Button, message, Form, Input, Spin } from "antd";
import { getToken, removeToken } from "../utils/storage";
import { ajax } from "../utils/request";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const { confirm } = Modal;

// Password change form
function PasswordChangeForm({ onClose, onLogoutSuccess }) {
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  const onFinish = async (values) => {
    try {
      const res = await ajax.patch("/user/me/password", values);
      if (res.data.code === 0) {
        messageApi.success("Password changed successfully. You'll be logged out for security.");
        onClose();
        removeToken().then(() => {
          if (onLogoutSuccess) {
            onLogoutSuccess();
          }
        });
      } else {
        messageApi.error(res.data.detail || res.data.msg);
      }
    } catch (err) {
      messageApi.error(err.response?.data?.detail || "Failed to change password. Please try again.");
    }
  };

  const passwordRules = [
    { required: true, message: 'Please input your password!' },
    { min: 6, message: 'Password must be between 6 and 18 characters long.' },
    { max: 18, message: 'Password must be between 6 and 18 characters long.' },
  ];

  return (
    <div>
      {contextHolder}
      <Form form={form} name="change_password" layout="vertical" onFinish={onFinish}>
        <Form.Item label="Old Password" name="old_password" rules={passwordRules}>
          <Input.Password />
        </Form.Item>
        <Form.Item label="New Password" name="new_password" rules={passwordRules}>
          <Input.Password />
        </Form.Item>
        <Form.Item
          label="Confirm New Password"
          name="confirm_password"
          dependencies={['new_password']}
          hasFeedback
          rules={[
            ...passwordRules,
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('new_password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error("The two passwords that you entered do not match!"));
              },
            }),
          ]}
        >
          <Input.Password />
        </Form.Item>
        <div className="flex flex-row items-center justify-center">
          <Button type="primary" htmlType="submit">
            Change Password
          </Button>
          <Button onClick={onClose} style={{ marginLeft: 38 }}>
            Cancel
          </Button>
        </div>
      </Form>
    </div>
  );
}

// Main Account modal
export default function Account({ isOpen, setOpen, onLogoutSuccess }) {
  const [messageApi, contextHolder] = message.useMessage();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isPasswordModalOpen, setPasswordModalOpen] = useState(false);
  const navigate = useNavigate();

  const fetchUserData = async () => {
    setLoading(true);
    try {
      const token = await getToken();
      if (!token) {
        setLoading(false);
        return;
      }
      const res = await ajax.get("/user/me");
      if (res.data && res.data.data) {
        setUserData(res.data.data);
      }
    } catch (err) {
      messageApi.error("Failed to load user data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchUserData();
    }
  }, [isOpen]);

  const handleCancel = () => {
    setOpen(false);
  };

  const handlePasswordModalOpen = () => {
    setPasswordModalOpen(true);
  };
  const handlePasswordModalClose = () => {
    setPasswordModalOpen(false);
  };

  const handleDeleteAccount = () => {
    confirm({
      title: "Are you sure you want to delete your account?",
      content: "All your data (username, email, chats,...) will be permanently deleted.",
      okText: "Yes, delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const res = await ajax.delete("/user/me");
          if (res.data.code === 0) {
            messageApi.success("Account deleted successfully. Redirecting...");
            removeToken().then(() => {
              if (onLogoutSuccess) onLogoutSuccess();
              setOpen(false);
              navigate("/");
            });
          } else {
            messageApi.error(res.data.msg || "Failed to delete account");
          }
        } catch (err) {
          messageApi.error(err.response?.data?.detail || "Error deleting account");
        }
      }
    });
  };

  const handleLogout = async () => {
        try {
            await onLogoutSuccess(); 
            messageApi.success({
                content: "Logout successful",
                duration: 3 });
            setOpen(false);
            navigate("/");
        } catch (error) {
            messageApi.error("Logout failed. Please try again.");
            console.error(error);
        }
    };

  return (
    <>
      {contextHolder}
      <Modal
        name="basic"
        title={
          <div className="flex justify-between items-center w-full">
            <span>My Account</span>
            <Button type="default" className="mr-10" onClick={handleLogout}>
              Logout
            </Button>
          </div>
        }
        centered={true}
        open={isOpen}
        footer={null}
        onCancel={handleCancel}
        width={450}
        style={{ maxWidth: '90%' }}
      >
        {loading || !userData ? (
          <div className="flex justify-center items-center p-8">
            <Spin size="large" />
          </div>
        ) : (
          <div className="mt-[25px]">
            <Form layout="vertical" initialValues={userData}>
              <Form.Item label="Username" name="username">
                <Input disabled style={{ color: 'rgba(0, 0, 0, 0.85)' }}/>
              </Form.Item>
              <Form.Item label="Email" name="email">
                <Input disabled style={{ color: 'rgba(0, 0, 0, 0.85)' }}/>
              </Form.Item>
              <div className="flex flex-row items-center justify-center gap-4 mt-6">
                <Button type="primary" onClick={handlePasswordModalOpen}>
                  Change Password
                </Button>
                <Button danger type="primary" onClick={handleDeleteAccount}>
                  Delete My Account
                </Button>
              </div>
            </Form>
          </div>
        )}
      </Modal>

      <Modal
        name="change-password"
        title="Change Password"
        centered={true}
        open={isPasswordModalOpen}
        footer={null}
        onCancel={handlePasswordModalClose}
      >
        <PasswordChangeForm onClose={handlePasswordModalClose} onLogoutSuccess={() => {
          if (onLogoutSuccess) onLogoutSuccess();
          setOpen(false);
          navigate("/");
        }} />
      </Modal>
    </>
  );
}